// Wrapper chrome for a device control screen.
//
// Two modes:
//  - Standalone (default): full-height page with an AppBar + hardware back — used
//    by the /device/:uniqueNumber route on mobile / small screens.
//  - Embedded: no AppBar, no min-height — used when a control screen is rendered
//    inside the desktop shell's right-side device panel (the panel supplies its
//    own title + close). Turn it on by wrapping the tree in
//    `<EmbeddedContext.Provider value={true}>`.

import React, { createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, AppBar, Toolbar, IconButton, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

export const EmbeddedContext = createContext(false);

export default function DeviceShell({ title, children, actions }) {
  const embedded = useContext(EmbeddedContext);
  const navigate = useNavigate();

  // ⚠️ THE TITLE IS SHOWN IN BOTH MODES. Embedded used to render the actions
  // and nothing else, on the reasoning that the surrounding panel supplies a
  // title — but the panel's title names the DEVICE, not what you are looking at
  // inside it, so a schedule list appeared with no indication of which output
  // it belonged to. A screen must always say what it is about, whatever chrome
  // is around it.
  if (embedded) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, pt: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>{title}</Box>
          {actions}
        </Box>
        <Box sx={{ p: 2, pt: 1 }}>{children}</Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100%', bgcolor: 'background.default' }}>
      <AppBar position="static" elevation={2}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          {/* `title` may be a node, not just a string — a breadcrumb needs two
              lines and its own wrapping. Centring is kept for the plain-string
              case by the wrapper, not by a Typography that would flatten a
              node into one line. */}
          <Box sx={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
            {typeof title === 'string'
              ? <Typography variant="h6" noWrap>{title}</Typography>
              : title}
          </Box>
          <Box sx={{ minWidth: 40, display: 'flex', justifyContent: 'flex-end' }}>{actions}</Box>
        </Toolbar>
      </AppBar>
      <Box sx={{ p: 2 }}>{children}</Box>
    </Box>
  );
}
